import BaseModal from 'components/CommonComponents/BaseModal'
import React, { useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { IDummyPop, IPop, RootState } from 'constants/interfaces'
import { Avatar, Box, Flex, Image, Portal, Text, useToast } from '@chakra-ui/core'
import { MarkUpText } from './MarkUpText'
import { PopType } from './utils'
import { getActionPopHandler } from './actionPopHandlers'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { btnShimmerCss, btnShimmerOverlayStyle } from 'components/ui/Button/shimmer'
import { Maximize2, PlayCircle } from 'lucide-react'
import useLocaleText from 'components/useLocaleText'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import FsLightbox from 'fslightbox-react'
import { isRtlSelector } from 'redux/selectors/general'

interface IProps {
  isOpen: boolean
  onClose: () => void
  pop?: IPop | IDummyPop | null
  handleSubmit: (popType: number | undefined) => void
}

interface IActionPopModalProps {
  isOpen: boolean
  onClose: () => void
  actionPop: IPop
  actionLabel: string
  descriptionLocaleKey: string
  titleLocaleKey?: string
  interpolateBodyIntoDescription?: boolean
  isLoading?: boolean
  onActionClick: () => void
}

const actionPopAnimationCss = `
  ${btnShimmerCss}
  @keyframes _action_avatar_ring_spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`

const actionPopJellyEnterCss = css`
  @keyframes _action_pop_jelly_enter {
    0% {
      transform: scale(0.74);
      opacity: 0;
      filter: blur(7px);
    }
    38% {
      transform: scale(1.06);
      opacity: 1;
      filter: blur(0);
    }
    56% {
      transform: scale(0.96);
    }
    72% {
      transform: scale(1.025);
    }
    86% {
      transform: scale(0.992);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  transform-origin: center center;
  animation: _action_pop_jelly_enter 760ms cubic-bezier(0.18, 0.89, 0.32, 1.28) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const isActionPop = (pop?: IPop | IDummyPop | null): pop is IPop => {
  return pop?.pop_type === PopType.ACTION_POP
}

const ActionPopMediaBlock = ({ imageUrl, videoUrl }: { imageUrl?: string | null; videoUrl?: string | null }) => {
  const [isOpenLightBox, setIsOpenLightBox] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const normalizedImageUrl = imageUrl?.trim()
  const normalizedVideoUrl = videoUrl?.trim()
  const mediaItems = useMemo(
    () => [
      ...(normalizedImageUrl ? [{ url: normalizedImageUrl, type: 'image' as const }] : []),
      ...(normalizedVideoUrl ? [{ url: normalizedVideoUrl, type: 'video' as const }] : []),
    ],
    [normalizedImageUrl, normalizedVideoUrl]
  )

  if (!mediaItems.length) return null

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setIsOpenLightBox((prev) => !prev)
  }

  return (
    <>
      <Flex
        mt="18px"
        flexDir={mediaItems.length === 1 ? 'column' : { base: 'column', sm: 'row' }}
        style={{ gap: '12px' }}
      >
        {mediaItems.map((media, index) => (
          <Box
            key={`${media.type}-${media.url}`}
            pos="relative"
            flex="1"
            minH="164px"
            borderRadius="18px"
            overflow="hidden"
            bg="white"
            border="1px solid #E6EAF5"
            boxShadow="0 10px 24px rgba(22, 29, 57, 0.08)"
          >
            {media.type === 'image' ? (
              <Image
                src={media.url}
                alt="Action pop media"
                w="100%"
                h="100%"
                minH="164px"
                maxH="260px"
                objectFit="cover"
                cursor="pointer"
                onClick={() => openLightbox(index)}
              />
            ) : (
              <video
                src={media.url}
                controls
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  minHeight: '164px',
                  maxHeight: '260px',
                  objectFit: 'cover',
                  background: '#111827',
                }}
              />
            )}

            <NoFocusButton
              aria-label="Open media"
              pos="absolute"
              top="10px"
              right="10px"
              minW="38px"
              w="38px"
              h="38px"
              p="0"
              borderRadius="14px"
              bg="rgba(17, 24, 39, 0.72)"
              color="white"
              border="1px solid rgba(255,255,255,0.22)"
              _hover={{ bg: 'rgba(17, 24, 39, 0.86)' }}
              onClick={() => openLightbox(index)}
            >
              {media.type === 'video' ? <PlayCircle size={19} /> : <Maximize2 size={18} />}
            </NoFocusButton>
          </Box>
        ))}
      </Flex>

      <Portal container={document.getElementById('root')}>
        <FsLightbox
          key={JSON.stringify(mediaItems)}
          toggler={isOpenLightBox}
          sources={mediaItems.map((media) => media.url)}
          sourceIndex={lightboxIndex}
          types={mediaItems.map((media) => media.type)}
        />
      </Portal>
    </>
  )
}

// Default: just the translated description. DRIVE_FOLDER (interpolateBodyIntoDescription) bold-appends the
// folder name from pop_body; other reference types let an admin-authored pop_body replace it outright.
const buildDescriptionContent = (
  descriptionLocaleText: string,
  popBody: string | undefined,
  interpolateBodyIntoDescription: boolean
): string => {
  if (interpolateBodyIntoDescription)
    return popBody ? `${descriptionLocaleText} <b>${popBody}</b>` : descriptionLocaleText
  return popBody || descriptionLocaleText
}

const ActionPopModal = ({
  isOpen,
  onClose,
  actionPop,
  actionLabel,
  descriptionLocaleKey,
  titleLocaleKey,
  interpolateBodyIntoDescription = false,
  isLoading = false,
  onActionClick,
}: IActionPopModalProps) => {
  const isRtl = useSelector(isRtlSelector)
  const theme = useSelector((state: RootState) => state.general.theme)
  const { retailUsersObject } = useSelector((state: RootState) => state.config.config)
  const elementsColor = theme?.elementsColor || '#879AF2'
  const creator = actionPop.created_by ? retailUsersObject?.[actionPop.created_by] : null
  const creatorName = creator
    ? `${creator.first_name || ''} ${creator.last_name || ''}`.trim()
    : actionPop.created_by || ''
  const avatarSrc = actionPop.profile_image_url || creator?.avatar_profile_img_url || creator?.profile_img_url
  const descriptionLocaleText = useLocaleText(descriptionLocaleKey)
  const popBody = actionPop.pop_body?.trim()
  const descriptionContent = buildDescriptionContent(descriptionLocaleText, popBody, interpolateBodyIntoDescription)
  const actionButtonText = actionPop.ack_btn_text?.trim() ? actionPop.ack_btn_text : actionLabel
  const actionLabelText = useLocaleText(actionButtonText)
  const popTitle = useLocaleText(actionPop.pop_title?.trim() || titleLocaleKey || '')

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={false}
      overflowY="unset"
      maxWidth="34rem"
      hideCloseButton={true}
      animationCss={actionPopJellyEnterCss}
      borderRadius="28px"
      backGroundModalContent="#F8FAFF"
      headerPaddingTop="0"
      headerContent={
        <Box
          pos="relative"
          minH="70px"
          style={{
            filter: 'drop-shadow(0 0 10px rgba(0, 0, 0, 0.1))',
          }}
        >
          <style>{actionPopAnimationCss}</style>
          <Box
            pos="absolute"
            left={isRtl ? '14px' : 'unset'}
            right={isRtl ? 'unset' : '14px'}
            top="-44px"
            w="112px"
            h="112px"
          >
            <Box
              aria-hidden="true"
              pos="absolute"
              top="-6px"
              left="-6px"
              right="-6px"
              bottom="-6px"
              borderRadius="50%"
              style={{
                background: `conic-gradient(from 0deg, ${elementsColor}, white 18%, ${elementsColor} 42%, ${elementsColor} 100%)`,
                animation: '_action_avatar_ring_spin 1.8s linear infinite',
                boxShadow: `0 10px 32px ${elementsColor}40`,
              }}
            />
            <Avatar
              name={creatorName || actionPop.pop_title}
              src={avatarSrc}
              w="112px"
              h="112px"
              bg="none"
              color="white"
              fontSize="34px"
              fontWeight="800"
              pos="relative"
              border="none"
            />
          </Box>
          {creatorName && (
            <Flex
              pos="absolute"
              left={isRtl ? '85px' : 'unset'}
              right={isRtl ? 'unset' : '85px'}
              top="20px"
              alignItems="center"
              minH="34px"
              px="16px"
              flexShrink={0}
              borderRadius="18px"
              bg="white"
              w="fit-content"
              style={{
                columnGap: '8px',
              }}
            >
              <Box w="8px" h="8px" borderRadius="50%" bg="#2ECC71" />
              <Text fontSize="15px" fontWeight="700" color="#111827" whiteSpace="nowrap" isTruncated maxW="250px">
                {creatorName}
              </Text>
            </Flex>
          )}
        </Box>
      }
      bodyContent={
        <Box px="34px" pt="0" pb="6px">
          <Box mb="24px">
            <Flex alignItems="flex-start" justifyContent="space-between" mb="9px" style={{ columnGap: '14px' }}>
              <Box lineHeight="1.2" minW={0} flex="1">
                <MarkUpText
                  text={popTitle}
                  initiator={actionPop.pop_initiator}
                  fontFamily="Arial"
                  lineHeight="31px"
                  fontSize="26px"
                  fontWeight="700"
                />
              </Box>
            </Flex>
            <Box color="#7B8494" lineHeight="1.45">
              <MarkUpText
                text={descriptionContent}
                initiator={actionPop.pop_initiator}
                fontFamily="Arial"
                lineHeight="21px"
                fontSize="15px"
              />
            </Box>
            <ActionPopMediaBlock imageUrl={actionPop.pop_image_url} videoUrl={actionPop.video} />
          </Box>
        </Box>
      }
      footerContent={
        <Box w="100%" px="10px">
          <NoFocusButton
            dir="auto"
            w="100%"
            background={elementsColor}
            pos="relative"
            overflow="hidden"
            isLoading={isLoading}
            isDisabled={isLoading}
            border="1px solid rgba(255, 255, 255, 0.28)"
            boxShadow={`0 10px 22px ${elementsColor}30`}
            _active={{ transform: 'scale(0.99)' }}
            _hover={{ opacity: 0.92 }}
            style={{
              fontSize: '14pt',
              fontFamily: 'Arial',
              marginBottom: '0',
              marginTop: '0',
              borderRadius: '18px',
              height: '52px',
              color: 'white',
              paddingTop: '0',
              paddingBottom: '0',
              textTransform: 'none',
            }}
            onClick={onActionClick}
          >
            {!isLoading && <Box style={btnShimmerOverlayStyle} />}
            <MarkUpText text={actionLabelText} initiator={actionPop.pop_initiator} />
          </NoFocusButton>
        </Box>
      }
    />
  )
}

export const ActionPop = ({ isOpen, onClose, pop, handleSubmit }: IProps) => {
  const navigate = useNavigate()
  const toast = useToast()
  const [isActionLoading, setIsActionLoading] = useState(false)
  const actionPop = isActionPop(pop) ? pop : null

  const actionPopHandler = useMemo(() => getActionPopHandler(actionPop?.reference_type), [actionPop])

  const handleActionClick = async () => {
    if (!actionPop) return

    setIsActionLoading(true)
    try {
      await actionPopHandler.execute({
        pop: actionPop,
        navigate,
      })

      handleSubmit(actionPop.pop_type)
    } catch (e) {
      console.error(e)
      toast({
        status: 'error',
        title: 'Error',
        description: 'Something went wrong',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsActionLoading(false)
    }
  }

  return (
    <>
      {actionPop && (
        <ActionPopModal
          isOpen={isOpen}
          onClose={onClose}
          actionPop={actionPop}
          actionLabel={actionPopHandler.actionLabel}
          descriptionLocaleKey={actionPopHandler.descriptionLocaleKey}
          titleLocaleKey={actionPopHandler.titleLocaleKey}
          interpolateBodyIntoDescription={actionPopHandler.interpolateBodyIntoDescription}
          isLoading={isActionLoading}
          onActionClick={handleActionClick}
        />
      )}
    </>
  )
}
